import {
  Album02Icon,
  BookOpen01Icon,
  Calendar01Icon,
  Coffee01Icon,
  File01Icon,
  Mic01Icon,
  Note01Icon,
  PaintBrush01Icon,
  Presentation01Icon,
  UserCheck01Icon,
  UserGroupIcon,
  Wrench01Icon
} from '@hugeicons/react'
/** Tom semântico do tipo de sessão — resolvido para token do DS na apresentação. */
export type ProgrammingKindTone =
  | 'brand'
  | 'warning'
  | 'info'
  | 'success'
  | 'neutral'

/**
 * Assinatura de ícone do Hugeicons, derivada de um ícone real em vez de
 * redigitada — `HugeiconsProps` não é reexportado pela raiz do pacote, e
 * escrever a assinatura à mão já divergiu uma vez do que a lib exporta.
 */
export type ProgrammingKindIcon = typeof Calendar01Icon

export type ProgrammingKindEntry = {
  slug: string
  nameKey: string
  icon: ProgrammingKindIcon
  tone: ProgrammingKindTone
}

/**
 * Ícone e tom de cada tipo de sessão — a camada de **apresentação** do tipo.
 *
 * Fonte do contrato — `api/app/models/programming.rb`:
 *   enum :kind, { talk: 0, workshop: 1, keynote: 2, interval: 3, check_in: 4,
 *                 full_papers: 5, short_papers: 6, red_showcase: 7,
 *                 round_table: 8, short_course: 9, practical_workshop: 10 }
 *
 * A **lista** de tipos disponíveis para filtrar não sai daqui: vem do catálogo
 * servido pela api (`GET .../programmings/kinds`), justamente para que um tipo
 * novo no enum apareça sem deploy de frontend. O que este mapa faz é vestir os
 * tipos que já conhecemos; o que ele não conhece cai em `UNKNOWN_KIND` e ainda
 * assim é exibido, com o rótulo que a api mandou.
 *
 * O mapa anterior indexava por `palestra` / `intervalo`, chaves que a api nunca
 * emitiu, e por isso `talk`, `interval` e `check_in` caíam no fallback e eram
 * exibidos em inglês cru ao participante. `model/kind.test.ts` trava esse
 * contrato contra o enum do Rails.
 */
export const PROGRAMMING_KINDS = [
  {
    slug: 'talk',
    nameKey: 'schedule.kinds.talk',
    icon: Mic01Icon,
    tone: 'brand'
  },
  {
    slug: 'workshop',
    nameKey: 'schedule.kinds.workshop',
    icon: Wrench01Icon,
    tone: 'warning'
  },
  {
    slug: 'keynote',
    nameKey: 'schedule.kinds.keynote',
    icon: Presentation01Icon,
    tone: 'info'
  },
  {
    slug: 'interval',
    nameKey: 'schedule.kinds.interval',
    icon: Coffee01Icon,
    tone: 'success'
  },
  {
    slug: 'check_in',
    nameKey: 'schedule.kinds.checkIn',
    icon: UserCheck01Icon,
    tone: 'neutral'
  },
  {
    slug: 'full_papers',
    nameKey: 'schedule.kinds.fullPapers',
    icon: File01Icon,
    tone: 'info'
  },
  {
    slug: 'short_papers',
    nameKey: 'schedule.kinds.shortPapers',
    icon: Note01Icon,
    tone: 'info'
  },
  {
    slug: 'red_showcase',
    nameKey: 'schedule.kinds.redShowcase',
    icon: Album02Icon,
    tone: 'brand'
  },
  {
    slug: 'round_table',
    nameKey: 'schedule.kinds.roundTable',
    icon: UserGroupIcon,
    tone: 'brand'
  },
  {
    slug: 'short_course',
    nameKey: 'schedule.kinds.shortCourse',
    icon: BookOpen01Icon,
    tone: 'warning'
  },
  {
    slug: 'practical_workshop',
    nameKey: 'schedule.kinds.practicalWorkshop',
    icon: PaintBrush01Icon,
    tone: 'warning'
  }
] as const satisfies readonly ProgrammingKindEntry[]

/**
 * Entrada usada quando a api emite um tipo ainda não mapeado.
 * Nunca ecoa o valor recebido — exibir o enum cru ao participante foi
 * justamente o defeito corrigido nesta refatoração.
 */
const UNKNOWN_KIND: ProgrammingKindEntry = {
  slug: 'unknown',
  nameKey: 'schedule.kinds.unknown',
  icon: Calendar01Icon,
  tone: 'neutral'
}

export const getProgrammingKind = (
  kind?: string | null
): ProgrammingKindEntry => {
  const slug = (kind ?? '').toLowerCase()
  return PROGRAMMING_KINDS.find(entry => entry.slug === slug) ?? UNKNOWN_KIND
}
